import { ReactElement } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { renderError } from 'components/common/ErrorFallback';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ChakraProvider } from '@chakra-ui/react';
import { queryClient } from 'utils/react-query';
import { system } from 'styles/theme';
import { Toaster } from 'components/ui/toaster';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { UsercentricsProvider } from 'consent/UsercentricsProvider';
import { TrackingInitializer } from 'consent/TrackingInitializer';
import { AlgoliaInstantSearchProvider } from './InstantSearchProvider';
import { TopBarProvider } from './TopBarProvider';
import { MetaProvider } from './MetaProvider';
import { BreadcrumbProvider } from './BreadcrumbProvider';
import { ModalContextProvider } from './ModalContextProvider';
import { ModalProvider } from './ModalProvider';
import { GOOGLE_AUTH_CLIENT_ID } from '../../config/env';
import { FeatureFlagsProvider } from './FeatureFlags';

type Props = {
  children: ReactElement | ReactElement[];
};

export const Providers = ({ children }: Props): ReactElement => {
  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider value={system}>
        <UsercentricsProvider>
          <TrackingInitializer />
          <ReactQueryDevtools />
          <FeatureFlagsProvider>
            <TopBarProvider>
              <MetaProvider>
                <ModalContextProvider>
                  <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
                    <ModalProvider>
                      <BreadcrumbProvider>
                        <AlgoliaInstantSearchProvider>
                          <ErrorBoundary fallbackRender={renderError}>{children}</ErrorBoundary>
                        </AlgoliaInstantSearchProvider>
                      </BreadcrumbProvider>
                    </ModalProvider>
                  </GoogleOAuthProvider>
                </ModalContextProvider>
              </MetaProvider>
            </TopBarProvider>
          </FeatureFlagsProvider>
          <Toaster />
        </UsercentricsProvider>
      </ChakraProvider>
    </QueryClientProvider>
  );
};
